/**
 * 🏆 WeeklyArenaPanel — Top 10 PvP weekly leaderboard + claim button.
 *
 * Mounted on the Arena page. Polls /arena/weekly/state once on mount.
 * Click "Claim Last Week" if your previous-week rank was in the Top 10.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtCountdown(secs) {
  if (secs == null || secs <= 0) return "now";
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

const RANK_STYLE = {
  1: { border: "border-amber-400", glow: "shadow-[0_0_18px_rgba(245,158,11,0.4)]", chip: "bg-amber-500/20 text-amber-200", icon: "🥇" },
  2: { border: "border-neutral-400", glow: "", chip: "bg-neutral-400/20 text-neutral-200", icon: "🥈" },
  3: { border: "border-orange-400", glow: "", chip: "bg-orange-500/20 text-orange-200", icon: "🥉" },
};

export default function WeeklyArenaPanel() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/arena/weekly/state`);
      setState(r.data);
    } catch (e) {
      // silent — panel hides itself on error
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const onClaim = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/arena/weekly/claim`);
      const rw = r.data?.rewards || {};
      const parts = [];
      if (rw.od) parts.push(`+${rw.od} 🪙 OD`);
      if (rw.life_ash) parts.push(`+${rw.life_ash} 💀 Ash`);
      if (rw.battle_coins) parts.push(`+${rw.battle_coins} 🟡 BC`);
      if (rw.chits) parts.push(`+${rw.chits} 🪙 Chits`);
      if (rw.eggs?.length) parts.push(`+${rw.eggs.length} 🥚 Epic+`);
      if (rw.title) parts.push(rw.title);
      toast.success(`🏆 Rank #${r.data.rank} claimed! ${parts.join(" · ")}`, { duration: 8000 });
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;
  const myRank = state.my_rank;
  const myWins = state.my_wins || 0;
  const qual = state.qualifier_wins;
  const winsToQual = Math.max(0, qual - myWins);
  const leaderboard = state.leaderboard || [];
  const visible = expanded ? leaderboard : leaderboard.slice(0, 5);

  return (
    <div className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-[#1a0820] via-[#0A0A0A] to-[#1a1208] p-4 sm:p-5 mb-5"
         data-testid="weekly-arena-panel">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-lg font-black tracking-wide text-amber-300" style={{ fontFamily: 'Anton, sans-serif' }}>
            🏆 WEEKLY ARENA · TOP 10 (PvP)
          </div>
          <div className="text-xs text-neutral-400">
            {state.week_label} · Resets in <span className="text-amber-300 font-bold">{fmtCountdown(state.seconds_to_reset)}</span>
          </div>
        </div>
        {state.can_claim_previous && (
          <button data-testid="weekly-claim-btn" disabled={busy} onClick={onClaim}
                  className="px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 disabled:opacity-50 text-black font-bold text-sm shadow-lg">
            🎁 Claim Last Week
          </button>
        )}
        {state.previous_already_claimed && (
          <span className="px-3 py-1.5 rounded-full bg-neutral-800 text-neutral-400 text-xs"
                data-testid="weekly-already-claimed">
            ✅ Last week claimed
          </span>
        )}
      </div>

      {/* My standing */}
      <div className="mb-3 rounded-lg border border-neutral-800 bg-black/40 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <span className="text-neutral-500">Your PvP wins this week: </span>
            <span className="font-bold text-amber-200" data-testid="my-wins">{myWins}</span>
            <span className="text-neutral-500"> / {qual} to qualify</span>
          </div>
          {myRank ? (
            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-bold"
                  data-testid="my-rank">
              Currently #{myRank}
            </span>
          ) : winsToQual > 0 ? (
            <span className="px-3 py-1 rounded-full bg-purple-700/30 text-purple-200 text-xs"
                  data-testid="my-needs-wins">
              {winsToQual} more wins to qualify
            </span>
          ) : (
            <span className="px-3 py-1 rounded-full bg-neutral-800 text-neutral-400 text-xs">
              Qualified — climb the ladder!
            </span>
          )}
        </div>
        {/* Progress bar */}
        <div className="mt-2 h-1.5 rounded-full bg-neutral-900 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-purple-500 to-amber-400 transition-all"
               style={{ width: `${Math.min(100, (myWins / qual) * 100)}%` }} />
        </div>
        {state.my_weekly_title && (
          <div className="mt-2 text-xs text-amber-300 font-bold" data-testid="my-title">
            {state.my_weekly_title}
          </div>
        )}
      </div>

      {/* Leaderboard */}
      {leaderboard.length === 0 ? (
        <div className="text-center text-neutral-500 text-sm py-6" data-testid="weekly-leaderboard-empty">
          No qualifiers yet this week. Be the first to hit {qual} PvP wins!
        </div>
      ) : (
        <div className="space-y-1.5" data-testid="weekly-leaderboard">
          {visible.map((row) => {
            const style = RANK_STYLE[row.rank] || {};
            return (
              <div key={row.user_id}
                   className={`flex items-center justify-between rounded-lg border ${style.border || "border-neutral-800"} bg-black/40 ${style.glow || ""} px-3 py-2 text-sm`}
                   data-testid={`weekly-row-${row.rank}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xl w-7 text-center">{style.icon || `#${row.rank}`}</span>
                  <div className="min-w-0">
                    <div className="font-bold truncate">
                      {row.name}
                      {row.handle && <span className="text-neutral-500 text-xs ml-1">@{row.handle}</span>}
                    </div>
                    {row.weekly_title && (
                      <div className="text-[10px] text-amber-400">{row.weekly_title}</div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-amber-200 font-bold">{row.wins}W</span>
                  {row.reward_preview && (
                    <RewardPreview rank={row.rank} preview={row.reward_preview} />
                  )}
                </div>
              </div>
            );
          })}
          {leaderboard.length > 5 && (
            <button onClick={() => setExpanded(!expanded)}
                    data-testid="weekly-expand-btn"
                    className="w-full text-xs text-neutral-400 hover:text-amber-200 py-2">
              {expanded ? "Show less ↑" : `Show all ${leaderboard.length} ↓`}
            </button>
          )}
        </div>
      )}

      {/* Reward ladder summary */}
      <details className="mt-3 text-xs">
        <summary className="cursor-pointer text-neutral-400 hover:text-amber-200" data-testid="rewards-summary-toggle">
          🎁 Full reward ladder
        </summary>
        <div className="mt-2 space-y-1 text-neutral-300">
          <div>🥇 #1 — 30 🪙 OD · 100 💀 Ash · 150 🟡 BC · 3 🥚 Epic+ · Phoenix Sovereign title</div>
          <div>🥈 #2 — 15 🪙 OD · 50 💀 Ash · 75 🟡 BC · 2 🥚 Epic+ · Steel Hand title</div>
          <div>🥉 #3 — 8 🪙 OD · 25 💀 Ash · 38 🟡 BC · 1 🥚 Epic+ · Iron Will title</div>
          <div>#4-10 — Chits only: 50/40/35/30/25/20/15</div>
        </div>
      </details>
    </div>
  );
}

function RewardPreview({ rank, preview }) {
  if (rank > 3) {
    return <span className="text-[10px] text-neutral-500">{preview.chits} 🪙</span>;
  }
  return (
    <span className="text-[10px] text-amber-300">
      {preview.od} OD · {preview.eggs} 🥚
    </span>
  );
}
